import {
  Button,
  ButtonMenu,
  ButtonModal,
  SelectField,
  useComputed,
  useGridTableApi,
  useGroupBy,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMatch } from "react-router";
import { createConstraintItemCatalogUrl, createTaskCatalogFormUrl } from "src/RouteUrls";
import { SearchBox } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, GlobalPlanTaskFilter } from "src/generated/graphqlTypes";
import { HeaderBreadcrumb, PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { useTaskCatalogPageMetadataQuery } from "src/routes/libraries/task-catalog/TaskCatalogPage.generated";
import { definedFilterValues, useQueryStorage } from "src/routes/projects/schedule-v2/table/filterUtils";
import { catalogTaskFolderPath, catalogTasksPath } from "src/routes/routesDef";
import { Row, TaskCatalogTable } from "./TaskCatalogTable";
import { AddGlobalPlanTagModal } from "./components/AddGlobalPlanTagModal";
import { BulkGlobalPlanTagsModal } from "./components/BulkGlobalPlanTagsModal";
import { MermaidLinkModal } from "./components/MermaidLinkModal";
import { TaskCatalogFilterModal } from "./components/TaskCatalogFilterModal";

export enum TaskCatalogGroupBy {
  Team = "Team",
  Tag = "Tag",
  None = "None",
}

type TaskCatalogPageConfig = {
  pageTitle: string;
  breadcrumbs: HeaderBreadcrumb[];
  folderFilter: Partial<GlobalPlanTaskFilter>;
};

export type CustomTaskCatalogPageFilter = GlobalPlanTaskFilter & { groupBy?: TaskCatalogGroupBy | undefined };

// set local filters to undefined as a default
const customTaskCatalogPageFilterDefault: CustomTaskCatalogPageFilter = {
  archived: undefined,
  id: undefined,
  name: undefined,
  tags: undefined,
  team: undefined,
  groupBy: undefined,
  project: undefined,
  applyToAllSchedules: undefined,
};

export function TaskCatalogPage() {
  const { data } = useTaskCatalogPageMetadataQuery();
  const { regions = [], globalPlanTags = [], enumDetails } = data ?? {};
  const isReadOnly = !useFeatureFlag(FeatureFlagType.TaskCatalogAdmin);

  const businessFunctionTypes = useMemo(() => enumDetails?.businessFunctionType ?? [], [enumDetails]);

  // Determine if we are on a folder page
  const match = useMatch({ path: catalogTaskFolderPath, end: false });
  const isFolderPage = match !== null;

  // Get page title, breadcrumbs and folder-level task filter, if necessary
  const { breadcrumbs, folderFilter, pageTitle } = useMemo((): TaskCatalogPageConfig => {
    const breadcrumbs = [{ href: catalogTasksPath, label: "Task Catalog" }];

    if (!isFolderPage) return { pageTitle: "Task Catalog", breadcrumbs, folderFilter: {} };

    const { folderId } = match.params;
    if (!folderId) return { pageTitle: "Task Catalog", breadcrumbs, folderFilter: {} };
    if (folderId.includes("globalPlanTag")) {
      const tag = globalPlanTags.find((tag) => tag.id === folderId);
      const pageTitle = `${tag?.name ?? "Tag"} Tasks`;
      return {
        pageTitle,
        breadcrumbs: [...breadcrumbs, { href: match.pathname, label: pageTitle }],
        folderFilter: { tags: [folderId] },
      };
    } else {
      const team = businessFunctionTypes.find((team) => team.code === folderId);
      const pageTitle = `${team?.name ?? "Team"} Tasks`;
      return {
        pageTitle,
        breadcrumbs: [...breadcrumbs, { href: match.pathname, label: pageTitle }],
        folderFilter: { team: team ? [team.code] : [] },
      };
    }
  }, [match, isFolderPage, globalPlanTags, businessFunctionTypes]);

  const [search, setSearch] = useState<string | undefined>();

  // custom hook that allows us to set queryParams to use as a filter (so groupBy will persist)
  const { queryStorage, setQueryStorage } = useQueryStorage({
    storageKey: "taskCatalogPageFilter",
    initialQueryStorage: customTaskCatalogPageFilterDefault,
  });

  // remove query param filters that are not hooked up to the backend
  const catalogTaskFilter = useMemo(() => mapToFilter(queryStorage), [queryStorage]);

  const groupBy = useGroupBy<TaskCatalogGroupBy>({ None: "None", Team: "Team", Tag: "Tag" });

  // Sync groupBy with queryStorage so it persists
  useEffect(() => {
    if (queryStorage.groupBy) {
      groupBy.setValue(queryStorage.groupBy);
    }
    // Intentionally not including "groupBy" as this causes a circular dependency error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryStorage.groupBy]);

  const showFilterModal = useCallback(
    (close: VoidFunction) => {
      return (
        <TaskCatalogFilterModal
          businessFunctionTypes={businessFunctionTypes}
          regions={regions}
          setFilter={setQueryStorage}
          globalPlanTags={globalPlanTags}
          filter={catalogTaskFilter}
        />
      );
    },
    [businessFunctionTypes, globalPlanTags, regions, setQueryStorage, catalogTaskFilter],
  );

  const api = useGridTableApi<Row>();
  const { openModal } = useModal();

  const selectedRows = useComputed(() => api.getSelectedRowIds("data"), [api]);

  // The selected rows will have `TagName`-`TaskId` or `TeamName`-`TaskId` ot just `TaskId` as id and we need to split them to get the task ids
  const selectedTaskIds = useMemo(
    () => selectedRows.map((id) => (id.includes("-") ? id.split("-")[1] : id)),
    [selectedRows],
  );

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumbs}
        title={pageTitle}
        right={!isReadOnly && <Button label="Create New" onClick={createTaskCatalogFormUrl()} />}
      />
      <TableActions onlyRight>
        {!isFolderPage && (
          <div>
            <SelectField
              label="Group By"
              value={groupBy.value}
              onSelect={(value) => {
                setQueryStorage({ ...catalogTaskFilter, groupBy: value });
              }}
              options={groupBy.options}
              labelStyle="inline"
              sizeToContent
            />
          </div>
        )}
        <ButtonModal content={showFilterModal} trigger={{ label: "Filter" }} />
        <SearchBox onSearch={setSearch} />
        <ButtonMenu
          trigger={{ icon: "verticalDots" }}
          items={[
            ...(!isReadOnly
              ? [
                  {
                    label: "Add to an Existing Tag",
                    onClick: () => openModal({ content: <BulkGlobalPlanTagsModal taskIds={selectedTaskIds} /> }),
                    disabled: selectedTaskIds.isEmpty,
                  },
                ]
              : []),
            ...(!isReadOnly
              ? [
                  {
                    label: "Add to a New Tag",
                    onClick: () => openModal({ content: <AddGlobalPlanTagModal taskIds={selectedTaskIds} /> }),
                    disabled: selectedTaskIds.isEmpty,
                  },
                ]
              : []),
            // {
            //   label: "Audit Tasks",
            //   // TODO: implement
            //   onClick: noop,
            //   disabled: true,
            // },
            // TODO: readonly version of this
            {
              label: isReadOnly ? "View Constraints" : "Manage Constraints",
              onClick: createConstraintItemCatalogUrl(),
            },
            { label: "Visualize Task Dependencies", onClick: () => openModal({ content: <MermaidLinkModal /> }) },
          ].compact()}
        />
      </TableActions>
      <TaskCatalogTable
        groupBy={groupBy.value}
        filter={{ name: search, ...folderFilter, ...definedFilterValues(catalogTaskFilter) }}
        api={api}
        isReadonly={isReadOnly}
      />
    </div>
  );
}

function mapToFilter(filter: CustomTaskCatalogPageFilter): GlobalPlanTaskFilter {
  // remove groupBy so we don't send it to the backend
  const { groupBy, ...others } = filter;
  return { ...others };
}
